// The four differentiator pillars, each its own full section, alternating sides.
// Product NAME is the hero; a category eyebrow sits above it and the benefit line
// sits below. Fusion + Templates use real ad renders from /feature-renders; Ad
// Strategist + AdPilot use self-contained CSS mockups (no image dependency).
//
// Copy locked in /homepage-message.md. Moat = "we do the whole loop, done better"
// — NOT "no one else does X". Tags say "most tools stop at X, we go further".

type PillarProps = {
  eyebrow: string; // short category label
  icon: string;
  product: string; // the hero — the product name
  sub: string; // benefit line under the name
  body: string;
  tag: string;
  flip?: boolean;
  dark?: boolean; // alternate section background for rhythm
  children: React.ReactNode; // the visual
};

function Pillar({ eyebrow, icon, product, sub, body, tag, flip, dark, children }: PillarProps) {
  return (
    <section className={`py-20 md:py-28 ${dark ? 'bg-surface' : 'bg-white'}`}>
      <div className="w-full px-6 md:px-10 lg:px-16">
        <div
          className={`flex flex-col ${flip ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 lg:gap-20 max-w-7xl mx-auto`}
        >
          {/* Visual — sits below the message on mobile (order-2), side-by-side on desktop */}
          <div className="order-2 lg:order-none w-full lg:w-1/2">{children}</div>

          {/* Copy — message first on mobile (order-1) */}
          <div className="order-1 lg:order-none w-full lg:w-1/2">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-light text-primary text-xs font-semibold tracking-wider uppercase mb-5">
              <i className={icon} />
              {eyebrow}
            </span>
            {/* Product name — the hero */}
            <h3 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-dark leading-[1.05] tracking-tight mb-4">
              {product}
            </h3>
            {/* Benefit line */}
            <p className="text-lg md:text-xl text-dark font-semibold leading-snug mb-4">
              {sub}
            </p>
            <p className="text-base md:text-lg text-dark-muted leading-relaxed mb-6">
              {body}
            </p>
            <div className="border-l-2 border-primary/40 pl-4 mb-0 lg:mb-8">
              <p className="text-sm md:text-base text-dark font-medium leading-relaxed">{tag}</p>
            </div>
            {/* CTA — sits with the copy on desktop only */}
            <a
              href="/auth/sign-up"
              className="hidden lg:inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary-hover text-white text-sm font-semibold rounded-full transition-colors shadow-md shadow-primary/15 group"
            >
              Start Free
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
            </a>
          </div>

          {/* CTA — mobile only, last (after the image) */}
          <a
            href="/auth/sign-up"
            className="order-3 lg:hidden flex w-full items-center justify-center gap-2 px-6 py-3.5 bg-primary hover:bg-primary-hover text-white text-base font-semibold rounded-full transition-colors shadow-md shadow-primary/15 group"
          >
            Start Free
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
}

const FUSION_IMAGES = [
  '/feature-renders/moodboard_3291e2b4e4-1ddd49ec.webp',
  '/feature-renders/moodboard_5c47495010-3ab99e30.webp',
  '/feature-renders/moodboard_86baf0c73b-bd30d591.webp',
  '/feature-renders/moodboard_aa60f3c1ff-732149e3.webp',
];

const TEMPLATE_IMAGES = [
  '/feature-renders/moodboard_95b559f834-26ca870a.webp',
  '/feature-renders/moodboard_274be8f757-11b74cb9.webp',
  '/feature-renders/moodboard_fc98c1fa54-18eea65d.webp',
  '/feature-renders/moodboard_231092c5ea-a2cf4a90.webp',
  '/feature-renders/moodboard_12f8516db9-7934695a.webp',
  '/feature-renders/moodboard_gen_4182429a62-8179aee0.webp',
];

export default function PillarSections() {
  return (
    <>
      {/* ============================= AD STRATEGIST ============================= */}
      <Pillar
        eyebrow="Competitor Research"
        icon="ri-search-eye-line"
        product="Ad Strategist"
        sub="See the ads already winning in your market."
        body="Ad Astronaut pulls your competitors' live ads from the ad libraries, finds the gaps, and writes the copy and concepts engineered to beat them. You launch on evidence, not guesses."
        tag="Ad-spy tools just show you competitor ads. We turn them into your next winning ad, with the copy and creative done for you."
      >
        {/* CSS mock: competitor intel → winning angle */}
        <div className="rounded-3xl border border-surface-dark/30 bg-white shadow-xl shadow-primary/5 p-6 md:p-7">
          <div className="flex items-center gap-2 mb-5">
            <i className="ri-radar-line text-primary" />
            <span className="text-sm font-semibold text-dark">Competitor ad intel</span>
            <span className="ml-auto text-xs text-dark-muted">3 ads analyzed</span>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-5">
            {['Ad 1', 'Ad 2', 'Ad 3'].map((label) => (
              <div key={label} className="text-center">
                <div className="aspect-[4/5] rounded-xl border border-surface-dark/40 bg-gradient-to-br from-surface to-surface-dark/20 flex items-center justify-center">
                  <i className="ri-image-line text-2xl text-dark-muted/40" />
                </div>
                <span className="mt-2 block text-xs text-dark-muted font-medium">{label}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-center mb-4">
            <i className="ri-arrow-down-line text-primary text-xl" />
          </div>
          <div className="rounded-xl border-2 border-primary/40 bg-primary-light/60 p-4">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary mb-2">
              <i className="ri-lightbulb-flash-line" /> Your winning angle
            </span>
            <p className="text-sm font-semibold text-dark leading-snug">
              "Visible results in 14 days. Half the price of the big brands."
            </p>
            <div className="mt-3 flex gap-2">
              <span className="text-[10px] px-2 py-1 rounded-full bg-white text-dark-muted border border-surface-dark/40">Hook</span>
              <span className="text-[10px] px-2 py-1 rounded-full bg-white text-dark-muted border border-surface-dark/40">Proof</span>
              <span className="text-[10px] px-2 py-1 rounded-full bg-white text-dark-muted border border-surface-dark/40">CTA</span>
            </div>
          </div>
        </div>
      </Pillar>

      {/* ================================= FUSION =============================== */}
      <Pillar
        eyebrow="Creative at Scale"
        icon="ri-magic-line"
        product="Fusion Engine"
        sub="A fresh, on-brand ad every time, automatically."
        body="The Fusion engine recombines proven creative recipes so every render is unique and on-brand. Beat ad fatigue and scale your creative without ever hiring a designer."
        tag="Most tools re-skin one template. Fusion builds a genuinely different, on-brand ad every time. None of that copy-paste look."
        flip
        dark
      >
        <div className="grid grid-cols-2 gap-4">
          {FUSION_IMAGES.map((src, i) => (
            <div
              key={src}
              className="relative rounded-2xl overflow-hidden aspect-square bg-white gradient-stroke shadow-lg shadow-primary/10"
            >
              <img src={src} alt={`Fusion variation ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
              <span className="absolute top-2 left-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/90 text-dark backdrop-blur-sm">
                Variation {i + 1}
              </span>
            </div>
          ))}
        </div>
      </Pillar>

      {/* =============================== TEMPLATES ============================== */}
      <Pillar
        eyebrow="Proven Creative"
        icon="ri-layout-masonry-line"
        product="Ad Recipes"
        sub="Proven recipes, not a blank canvas."
        body="Hundreds of curated, high-converting scenes and layouts. Pick a look, drop in your product, and get a designer-quality ad in seconds. No prompt engineering required."
        tag="These aren't generic canvas templates. They're ad recipes tuned to convert, curated for ecommerce."
      >
        <div className="grid grid-cols-3 gap-3">
          {TEMPLATE_IMAGES.map((src, i) => (
            <div
              key={src}
              className="rounded-xl overflow-hidden aspect-square bg-white gradient-stroke shadow-md"
            >
              <img src={src} alt={`Ad recipe ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
            </div>
          ))}
        </div>
      </Pillar>

      {/* =============================== AD PILOT =============================== */}
      <Pillar
        eyebrow="Auto-Publishing"
        icon="ri-flight-takeoff-line"
        product="AdPilot"
        sub="Your ads publish themselves."
        body="AdPilot schedules and posts to Facebook & Instagram on a steady cadence. That consistent presence grows your audience and keeps you compliant, with zero manual work."
        tag="Most tools stop at the download button. We take it all the way to scheduled-and-posted."
        flip
        dark
      >
        {/* CSS mock: an auto-scheduled ad calendar */}
        <div className="rounded-3xl border border-surface-dark/30 bg-white shadow-xl shadow-primary/5 p-6 md:p-7">
          <div className="flex items-center gap-2 mb-5">
            <i className="ri-calendar-schedule-line text-primary" />
            <span className="text-sm font-semibold text-dark">Your ad calendar</span>
            <span className="ml-auto inline-flex items-center gap-1 text-xs text-emerald-600 font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Auto-pilot on
            </span>
          </div>
          <div className="grid grid-cols-7 gap-1.5 mb-4">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
              <span key={i} className="text-[10px] text-center text-dark-muted font-medium">{d}</span>
            ))}
            {Array.from({ length: 28 }).map((_, i) => {
              const scheduled: Record<number, 'fb' | 'ig'> = { 3: 'ig', 8: 'fb', 12: 'ig', 17: 'fb', 22: 'ig', 26: 'fb' };
              const kind = scheduled[i];
              return (
                <div
                  key={i}
                  className={`aspect-square rounded-md flex items-center justify-center ${
                    kind ? 'bg-primary-light' : 'bg-surface'
                  }`}
                >
                  {kind === 'fb' && <i className="ri-facebook-circle-fill text-[#1877F2] text-sm" />}
                  {kind === 'ig' && <i className="ri-instagram-line text-primary text-sm" />}
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between rounded-xl bg-surface p-3">
            <span className="text-xs text-dark-muted">6 ads scheduled this month</span>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-dark">
              <i className="ri-facebook-circle-fill text-[#1877F2]" />
              <i className="ri-instagram-line text-primary" />
              FB + Instagram
            </span>
          </div>
        </div>
      </Pillar>
    </>
  );
}
